import { Loader2, Square } from "lucide-react";
import React from "react";
import { BaseChatBubble, ChatBubbleMessage } from "src/components/ui/chat/chat-bubble";

import TierZeroAvatar from "./TierZeroAvatar";

interface AgentLoadingBubbleProps {
  timestamp?: string;
  hideAvatar?: boolean;
  onInterrupt?: () => void;
  isInterrupting?: boolean;
  statusMessage?: string | null;
}

const PROGRESS_MESSAGES = [
  "Preparing and initializing runtime...",
  "Waiting for agent startup...",
];

// Standalone loading agent message component
const AgentLoadingBubble = React.memo<AgentLoadingBubbleProps>(function AgentLoadingBubble({
  timestamp,
  hideAvatar = false,
  onInterrupt,
  isInterrupting = false,
  statusMessage
}) {
  // Progress through messages sequentially, then stop at last message
  const [messageIndex, setMessageIndex] = React.useState(0);
  const fallbackMessage = PROGRESS_MESSAGES[messageIndex];

  // Use statusMessage from backend if provided, otherwise use fallback cycling messages
  const currentMessage = statusMessage || fallbackMessage;

  React.useEffect(() => {
    // Skip cycling if backend is providing the status message
    if (statusMessage) {
      return;
    }

    // If we've reached the last message, stop progressing
    if (messageIndex >= PROGRESS_MESSAGES.length - 1) {
      return;
    }

    // Move to next message after a random delay (5 to 8 seconds)
    const randomDelay = 5000 + Math.random() * 3000;
    const timeout = setTimeout(() => {
      setMessageIndex(prev => Math.min(prev + 1, PROGRESS_MESSAGES.length - 1));
    }, randomDelay);

    return () => clearTimeout(timeout);
  }, [messageIndex, statusMessage]);

  return (
    <BaseChatBubble variant="received" className="w-full">
      {!hideAvatar && (
        <div className="hidden md:flex flex-shrink-0 w-8 h-8 rounded-lg overflow-hidden">
          <TierZeroAvatar />
        </div>
      )}
      <div className="flex flex-col w-full min-w-0">
        <ChatBubbleMessage variant="received">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-primary flex-shrink-0 animate-pulse">●</span>
              <span className="text-muted-foreground animate-pulse">{currentMessage}</span>
            </div>
            {onInterrupt && (
              <>
                <hr className="my-3 border-t border-border/50" />
                <div className="flex items-center gap-2">
                  <button
                    className="h-8 w-8 flex items-center justify-center rounded-full transition-colors duration-200 text-red-400 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={onInterrupt}
                    disabled={isInterrupting}
                    title={isInterrupting ? "Stopping..." : "Stop thinking"}
                  >
                    {isInterrupting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Square className="w-4 h-4 fill-current" />
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </ChatBubbleMessage>
        {!hideAvatar && timestamp && (
          <div className="hidden md:block text-xs mt-1 text-left text-muted-foreground transition-opacity whitespace-nowrap">
            {timestamp}
          </div>
        )}
      </div>
    </BaseChatBubble>
  );
});

export default AgentLoadingBubble;
